import { FatturaEmessaDto, ProjectLightInfoDto } from '@api/client';
import { Select } from 'antd';
import { useAppDispatch } from 'core/store/hooks';
import { associateFatturaProject } from 'pages/private/active-cycle/active-cycle-bills/_redux/actions';
import { useEffect, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';
import NoData from 'shared/design-system/components/no-data-box';
import { formatCurrency } from 'shared/utils/common.utils';
import { disassociateInvoiceActiveCycle, searchActiveOrders } from '../../../_redux/actions';
import ModalAction from 'pages/private/project-list/project-detail/_components/modal-action';

interface Props {
  invoices: FatturaEmessaDto[];
  associatedProjects?: ProjectLightInfoDto[];
  idOrdine?: number | undefined;
  idOrdineSf?: string | undefined;
  showAddInvoice?: boolean;
}

type FatturaEmessaDtoWithProject = FatturaEmessaDto & { projectId: number | undefined };

const ActiveOrderAssociatedInvoicesTable: React.FC<Props> = ({
  invoices,
  associatedProjects,
  idOrdine,
  idOrdineSf,
  showAddInvoice,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [invoicesList, setInvoicesList] = useState<FatturaEmessaDtoWithProject[]>([]);
  const [dissociateInvoiceModalVisible, setDissociateInvoiceModalVisible] = useState<boolean>(false);
  const [invoiceToDeleteId, setInvoiceToDeleteId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (invoices) {
      setInvoicesList(invoices.map((invoice) => ({ ...invoice, projectId: undefined })));
    }
  }, [invoices]);

  useEffect(() => {
    console.log('invoicesList', invoicesList);
  }, [invoicesList]);

  const handleAssociateInvoice = (invoice: FatturaEmessaDtoWithProject) => {
    dispatch(
      associateFatturaProject({
        associaFattureProgettoDtoList: {
          fattureProgettoDtoList: [
            { pkFattura: invoice.idFattura, dettagli: [{ idProject: invoice.projectId, idOrdine }] },
          ],
        },
      })
    )
      .unwrap()
      .then((res) => {
        // if (res?.fattureProgettoDtoList) {
        AppToastService.success(t(`Invoice associated successfully`));
        dispatch(searchActiveOrders({ contractOrderSearchDto: { cicloAttivoOrderNumber: idOrdineSf } }));
        // }
      });
  };

  const handleChangeProjectId = (projectId: number | undefined, invoiceId: string | undefined) => {
    setInvoicesList(
      invoicesList.map((invoice) => {
        if (invoice.idFattura === invoiceId) {
          return { ...invoice, projectId };
        }
        return invoice;
      })
    );
  };

  const handleDissociateInvoice = () => {
    if (!invoiceToDeleteId) return;
    dispatch(disassociateInvoiceActiveCycle(invoiceToDeleteId)).then(() => {
      setDissociateInvoiceModalVisible(false);
      dispatch(searchActiveOrders({ contractOrderSearchDto: { cicloAttivoOrderNumber: idOrdineSf } }));
    });
  };

  if (invoicesList.length) {
    return (
      <div className="table-responsive table-bordered mt-3">
        <Table className={'mt-0 mb-0'}>
          <thead className="bg-red">
            <tr>
              <th scope="col">
                <span>{'N.Invoice'}</span>
              </th>
              {!showAddInvoice && (
                <th scope="col">
                  <span>{'ID Project'}</span>
                </th>
              )}
              <th scope="col">
                <span>{'Amount'}</span>
              </th>
              <th scope="col">
                <span>{'Invoice date'}</span>
              </th>
              <th scope="col">
                <span>{'Typology'}</span>
              </th>
              <th scope="col">
                <span>{'Account'}</span>
              </th>
              <th scope="col">
                <span>{'Expiry date'}</span>
              </th>
              <th scope="col">
                <span>{'Payment date'}</span>
              </th>
              <th scope="col" style={{ width: '350px' }}>
                <span>{'Description'}</span>
              </th>
              <th scope="col">
                <span>{'Status'}</span>
              </th>
              {showAddInvoice && (
                <>
                  <th scope="col" style={{ maxWidth: '50px' }}>
                    <span>{'Project'}</span>
                  </th>
                  <th scope="col" style={{ maxWidth: '50px' }}>
                    <span>{'Add'}</span>
                  </th>
                </>
              )}
              {!showAddInvoice && (
                <>
                  <th scope="col" style={{ maxWidth: '65px' }}>
                    <span>{'Delete'}</span>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {invoicesList.map((invoice) => (
              <tr key={`invoice-${invoice.idFattura}`}>
                <td className="text-end">
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.numeroFattura}</p>
                  </span>
                </td>
                {!showAddInvoice && (
                  <td className="text-end">
                    <span className="d-inline-flex align-items-center">
                      <p className="my-auto">{invoice.fkProgettoIntranet}</p>
                    </span>
                  </td>
                )}
                <td className="text-end">
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{formatCurrency(invoice.importo)}€</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.dataFattura?.toLocaleDateString('it-IT') || '-'}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.tipoFattura}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.denominazioneCliente}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.dataScadenza?.toLocaleDateString('it-IT') || '-'}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.dataPagamento?.toLocaleDateString('it-IT') || '-'}</p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto" title={invoice.descrizione || '-'}>
                      {invoice.descrizione
                        ? `${invoice.descrizione.slice(0, 100)}${invoice.descrizione.length > 100 ? '…' : ''}`
                        : '-'}
                    </p>
                  </span>
                </td>
                <td>
                  <span className="d-inline-flex align-items-center">
                    <p className="my-auto">{invoice.fattStatoContabile || '-'}</p>
                  </span>
                </td>
                {showAddInvoice && (
                  <>
                    <td>
                      <Select
                        className="w-100"
                        placeholder={'Select project'}
                        showSearch
                        optionFilterProp="children"
                        filterOption={true}
                        value={invoice.projectId}
                        allowClear
                        variant="borderless"
                        onChange={(event) => handleChangeProjectId(event, invoice.idFattura)}
                      >
                        {associatedProjects?.map((el) => (
                          <Select.Option key={`project-to-associate${el.projectId}`} value={el.projectId}>
                            {el.intranetId}
                          </Select.Option>
                        ))}
                      </Select>
                    </td>
                    <td className="text-center">
                      <Button
                        className="btn text-blue-links border-none p-0"
                        type="button"
                        variant="icon"
                        disabled={!invoice.projectId}
                        onClick={() => handleAssociateInvoice(invoice)}
                      >
                        <i className="fas fa-plus-circle text-blue-links" />
                      </Button>
                    </td>
                  </>
                )}
                {!showAddInvoice && (
                  <>
                    <td className="text-center">
                      <Button
                        className="btn text-blue-links border-none p-0"
                        type="button"
                        variant="icon"
                        onClick={() => {
                          setDissociateInvoiceModalVisible(true);
                          setInvoiceToDeleteId(invoice.idFattura);
                        }}
                      >
                        <i className="fas fa-xmark text-danger" />
                      </Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
        <ModalAction
          title={t('Dissociate invoice')}
          actionLabel={t('common.confirm')}
          cancelLabel={t('common.cancel')}
          action={() => {
            handleDissociateInvoice();
          }}
          setShow={(value) => setDissociateInvoiceModalVisible(value)}
          show={dissociateInvoiceModalVisible}
        >
          <span className="text-danger">
            <i className="fa-solid fa-triangle-exclamation"></i> <b>{t('common.attention')}</b>{' '}
          </span>
          <div className="p-1">
            <p>{t('Are you sure you want to dissociate the invoice?')}</p>
          </div>
        </ModalAction>
      </div>
    );
  } else {
    return (
      <div className="d-flex justify-content-center mt-3">
        <NoData msg={t('No invoices found')} />
      </div>
    );
  }
};

export default ActiveOrderAssociatedInvoicesTable;
